import axios from "axios";
import { getCurrentTenant } from "./tenantConfig";
import KEYCLOAK_CONFIG from "./keycloakConfig";

const { apiBaseUrl: API_BASE_URL, API_USERS_HOST } = getCurrentTenant();

// Axios instance oluştur
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  testURL: API_USERS_HOST,
  timeout: 120000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token refresh için mutex — aynı anda sadece 1 refresh olsun
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(token) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function onTokenRefreshFailed() {
  refreshSubscribers.forEach((cb) => cb(null));
  refreshSubscribers = [];
}

// Kullanıcıyı login'e yönlendir
function forceLogout() {
  console.log(
    "[AxiosInterceptor] !!! FORCE LOGOUT — clearing storage, redirecting to /login !!!",
  );
  localStorage.clear();
  window.location.href = "/login";
}

// Request Interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const projectCode = localStorage.getItem("projectCode")?.trim();
    const authToken = localStorage.getItem("authToken");

    // Project code'u URL'e ekle
    if (projectCode && !config.url.includes(projectCode)) {
      config.url = `/${projectCode}${config.url}`;
    }

    // Auth token'ı header'a ekle
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    // Request'i logla (development için)
    if (process.env.NODE_ENV === "development") {
      console.log("🚀 Request:", config.method.toUpperCase(), config.url);
    }

    return config;
  },
  (error) => {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
  },
);

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Response'u logla (development için)
    if (process.env.NODE_ENV === "development") {
      console.log("✅ Response:", response.config.url, response.status);
    }

    // API'niz returnCode kullanıyorsa, burada kontrol et
    if (response.data && response.data.returnCode !== undefined) {
      if (response.data.returnCode === 0) {
        // Başarılı yanıt - sadece data'yı döndür
        return response.data.data;
      } else {
        const exMessage = response.data.exMessage || "";

        // Backend HTTP 200 döndü ama auth hatası varsa → logout
        const authKeywords = [
          "no auth",
          "noauth",
          "unauthorized",
          "token",
          "auth fail",
          "authentication",
          "yetkisiz",
          "oturum",
        ];
        const isAuthError = authKeywords.some((kw) =>
          exMessage.toLowerCase().includes(kw),
        );

        if (isAuthError) {
          console.log(
            "[AxiosInterceptor] Auth error in returnCode response:",
            exMessage,
          );
          forceLogout();
          return Promise.reject(new Error(exMessage));
        }

        // API'den gelen normal hata
        const error = new Error(exMessage || "İşlem başarısız oldu");
        error.returnCode = response.data.returnCode;
        error.response = response;
        throw error;
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    console.log("[AxiosInterceptor] === ERROR INTERCEPTED ===");
    console.log("[AxiosInterceptor] Timestamp:", new Date().toISOString());
    console.log("[AxiosInterceptor] URL:", originalRequest?.url);
    console.log("[AxiosInterceptor] Status:", status);
    console.log("[AxiosInterceptor] Error message:", error.message);

    // 401 veya 403 — Token süresi dolmuş veya yetki hatası
    if ((status === 401 || status === 403) && !originalRequest._retry) {
      console.log(
        `[AxiosInterceptor] !!! ${status} DETECTED — attempting token refresh !!!`,
      );
      originalRequest._retry = true;

      // Zaten refresh yapılıyorsa, bekleyen kuyruğa ekle
      if (isRefreshing) {
        console.log(
          "[AxiosInterceptor] Refresh already in progress, queuing request...",
        );
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(axiosInstance(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");
        const companyName = localStorage.getItem("companyName");
        console.log("[AxiosInterceptor] refreshToken exists:", !!refreshToken);

        if (refreshToken) {
          const params = new URLSearchParams({
            grant_type: "refresh_token",
            client_id: KEYCLOAK_CONFIG.clientId,
            refresh_token: refreshToken,
          });

          console.log(
            "[AxiosInterceptor] Attempting to refresh token via Keycloak (DefaultClient)...",
          );

          const response = await fetch(
            `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
            {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              body: params,
            },
          );

          const data = await response.json();
          console.log(
            "[AxiosInterceptor] Refresh response status:",
            response.status,
          );

          if (data.access_token) {
            console.log("[AxiosInterceptor] DefaultClient refresh SUCCESS");

            if (data.refresh_token) {
              localStorage.setItem("refreshToken", data.refresh_token);
            }

            let finalToken = data.access_token;

            // companyName varsa token exchange yap
            if (companyName && companyName !== KEYCLOAK_CONFIG.clientId) {
              console.log(
                "[AxiosInterceptor] Doing token exchange for:",
                companyName,
              );
              const exchangeParams = new URLSearchParams({
                grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
                client_id: KEYCLOAK_CONFIG.clientId,
                subject_token: data.access_token,
                audience: companyName,
                requested_token_type:
                  "urn:ietf:params:oauth:token-type:access_token",
              });

              const exchangeRes = await fetch(
                `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                  },
                  body: exchangeParams,
                },
              );

              const exchangeData = await exchangeRes.json();

              if (exchangeData.access_token) {
                console.log("[AxiosInterceptor] Token exchange SUCCESS");
                finalToken = exchangeData.access_token;
                if (exchangeData.refresh_token) {
                  localStorage.setItem(
                    "refreshToken",
                    exchangeData.refresh_token,
                  );
                }
              } else {
                console.log(
                  "[AxiosInterceptor] Token exchange failed, using DefaultClient token",
                );
              }
            }

            localStorage.setItem("authToken", finalToken);
            isRefreshing = false;
            onTokenRefreshed(finalToken);

            // Orijinal isteği yeni token ile tekrarla
            originalRequest.headers.Authorization = `Bearer ${finalToken}`;
            return axiosInstance(originalRequest);
          } else {
            console.log(
              "[AxiosInterceptor] Refresh failed:",
              data.error,
              data.error_description,
            );
          }
        }

        // Token yenilenemedi → logout
        isRefreshing = false;
        onTokenRefreshFailed();
        forceLogout();
        return Promise.reject(error);
      } catch (refreshError) {
        console.log("[AxiosInterceptor] !!! REFRESH EXCEPTION !!!");
        console.log("[AxiosInterceptor] Refresh error:", refreshError.message);
        isRefreshing = false;
        onTokenRefreshFailed();
        forceLogout();
        return Promise.reject(refreshError);
      }
    }

    // 404 Not Found
    if (status === 404) {
      console.error("🔍 Kaynak bulunamadı:", error.config.url);
    }

    // 500 Server Error
    if (status >= 500) {
      console.error("🔥 Sunucu hatası:", status);
    }

    // Network Error
    if (!error.response) {
      console.error("📡 Ağ hatası veya sunucuya erişilemiyor");
    }

    console.error("❌ Response Error:", error.message);
    return Promise.reject(error);
  },
);

export default axiosInstance;
